
import React from 'react';
import { MessageCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const ThreoAIPricingFAQ = () => {
  const faqs = [
    {
      question: "What exactly is ThreoAI?",
      answer: "ThreoAI is a secure, customizable AI portal for businesses. It allows your team to chat with advanced AI models, analyze files, search the web in real time, and access internal company knowledge — all in one branded workspace. It's powered by enterprise-grade infrastructure and designed for real business workflows."
    },
    {
      question: "How is ThreoAI different from public AI tools like ChatGPT or Claude?",
      answer: "ThreoAI is purpose-built for companies — not consumers. Unlike public chatbots, it offers:\n• Secure team-based hosting with Zero Data Retention (ZDR)\n• Custom branding and company-specific memory\n• Access to multiple AI models\n• Team-wide collaboration\n• Optional embedded AI agents to automate real tasks\n\nYou're not just chatting with an AI — you're turning it into part of your business operations."
    },
    {
      question: "What AI models can I use in ThreoAI?",
      answer: "You can choose from multiple top-tier models including GPT-4 (via Azure), Claude, Gemini, and others.\n• Team plans can choose 1 model\n• Business plans can use up to 4 at once\n• Enterprise plans can access unlimited models, including bring-your-own options\n\nThis lets you tailor the AI to your needs — and even mix models across users or teams."
    },
    {
      question: "What does 'multi-tenant architecture' mean?",
      answer: "Every ThreoAI customer gets their own secure, isolated instance. That means:\n• Separate branding, users, and memory\n• Data is never shared between tenants\n• It scales as you grow, with per-client segmentation in Enterprise plans"
    },
    {
      question: "Can I brand the ThreoAI interface with my company's identity?",
      answer: "Yes — all plans include branding options like your logo, color scheme, and welcome prompts.\n\nEnterprise plans also include:\n• Custom domain (e.g., ai.yourcompany.com)\n• Role-based user segmentation (for departments or client-facing use)"
    },
    {
      question: "How does ThreoAI handle data security and privacy?",
      answer: "Security is built in from the start:\n• All data is processed via Microsoft Azure's OpenAI environment\n• Zero Data Retention (ZDR) enforced by default\n• Inherits SOC 2, HIPAA, and GDPR compliance from Azure\n\nEnterprise customers can also configure:\n• Custom data retention policies\n• SSO, SCIM, and region-specific hosting"
    },
    {
      question: "How does memory (RAG) work in ThreoAI?",
      answer: "ThreoAI uses Retrieval-Augmented Generation (RAG) to store and recall knowledge from your business:\n• Each user gets personal memory in every plan\n• Business plans add team-wide memory\n• Enterprise plans offer full company-wide memory and knowledge graphs\n\nYour memory works across any AI model, so you're not locked in."
    },
    {
      question: "What types of AI agents can I deploy on ThreoAI?",
      answer: "You can deploy two types of embedded agents:\n• ThreoAssistants — outcome-based agents that complete specific tasks (e.g., reporting, ticket handling, billing help)\n• ThreoAgents — conversational agents that interact in natural language across varied tasks\n\nAgents are built using our no-code Synthero Agent Builder and embedded into your portal. Add-on or included based on plan."
    },
    {
      question: "Do I need technical expertise to implement ThreoAI?",
      answer: "Not at all. Most customers rely on our team to configure their portal, upload documents, and embed AI agents.\n• Business plans include white-glove setup\n• Enterprise customers can build agents directly through our no-code builder"
    },
    {
      question: "Can ThreoAI integrate with our existing systems?",
      answer: "Yes. ThreoAI supports:\n• Secure data uploads (PDF, CSV, DOCX, etc.)\n• API integrations into your CRM, ticketing, or ERP tools\n• Agent-based automations that can fetch or send data\n\nEnterprise plans include full API access and custom connectors."
    },
    {
      question: "What are 'monthly conversations' in your pricing plans?",
      answer: "Actually — there are none.\nAll ThreoAI plans include unlimited usage under our fair use policy. You're free to:\n• Chat as often as needed\n• Upload files daily\n• Use real-time search and agents\n\nWe don't meter tokens or throttle usage unless abuse is detected."
    },
    {
      question: "Can we manage different user roles or departments?",
      answer: "Yes. Enterprise plans support:\n• User segmentation\n• Role-based permissions\n• Custom experiences per department, location, or client\n\nThis makes ThreoAI scalable and compliant across more complex teams."
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-synthreo-black">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center gap-3 mb-8">
          <MessageCircle size={24} className="text-synthreo-blue-light" />
          <h2 className="text-2xl md:text-3xl font-bold font-gilroy text-center">
            Frequently Asked Questions
          </h2>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="border-b border-gray-700 pb-4 last:border-b-0"
              >
                <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                  <span className="text-left">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-300 leading-relaxed pt-2 whitespace-pre-wrap text-left">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export default ThreoAIPricingFAQ;
